import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Namespace, useT } from "@racoon/i18n";
import { Input, cn } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { BrandIcPlus } from "@racoon/icons";
import { Pencil, ArrowRight, Search } from "lucide-react";

import { useListOrganizationsQuery } from "~/shared/api/organization/organization.grpc";
import { useOrganizationsUrlState, tabToType } from "../model/useOrganizationsUrlState";
import { useDynamicPageSize } from "../model/useDynamicPageSize";
import { OrganizationDialog } from "./OrganizationDialog";
import { Pagination } from "~/shared/ui/pagination/pagination";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";

const { OrganizationType } = ConnectProto;

const getTypeLabel = (type: ConnectProto.OrganizationType, t: ReturnType<typeof useT>) => {
  switch (type) {
    case OrganizationType.SERVICE_PROVIDER: return t(Namespace.Organizations, "type.provider");
    case OrganizationType.SERVICE_RECEIVER: return t(Namespace.Organizations, "type.receiver");
    case OrganizationType.DISPOSAL_SITE_OPERATOR: return t(Namespace.Organizations, "type.disposal");
    case OrganizationType.GOVERNMENT: return t(Namespace.Organizations, "type.government");
    default: return t(Namespace.Organizations, "type.default");
  }
};

const getBadgeColor = (type: ConnectProto.OrganizationType) => {
  switch (type) {
    case OrganizationType.SERVICE_PROVIDER: return "bg-[#007aff]";
    case OrganizationType.SERVICE_RECEIVER: return "bg-[#34c759]";
    case OrganizationType.GOVERNMENT: return "bg-[#ff3b30]";
    case OrganizationType.DISPOSAL_SITE_OPERATOR: return "bg-[#5856d6]";
    default: return "bg-[#007aff]";
  }
};

export default function PlatformOrganizationsPage() {
  const t = useT();
  const nav = useNavigate();
  const { select } = useSelectedCompany();
  const [addOpen, setAddOpen] = React.useState(false);
  const [editOrg, setEditOrg] = React.useState<ConnectProto.Organization | null>(null);

  const cardContainerRef = React.useRef<HTMLDivElement>(null);
  const pageSize = useDynamicPageSize(cardContainerRef);

  const [sp] = useSearchParams();
  const currentTab = sp.get("tab") || "all";
  const searchQuery = sp.get("q") || "";
  const targetType = tabToType[currentTab];

  const pageParam = parseInt(sp.get("page") || "1", 10);
  const currentPage = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

  const { data: orgList, isLoading, isFetching } = useListOrganizationsQuery(
    {
      ...(targetType !== undefined && { type: targetType }),
      ...(searchQuery && { searchQuery }),
      listFilter: { currentPage: currentPage - 1, pageSize },
    },
    { skip: pageSize === 0 },
  );

  const organizations = orgList?.organizations ?? [];
  const totalItems = orgList?.listInfo?.totalItems ?? 0;

  const { states, selectedState, onStateChange, onSearchChange, searchValue, onPageChange, totalPages } =
    useOrganizationsUrlState(totalItems, pageSize);

  const onCardClick = (org: ConnectProto.Organization) => {
    select({ id: Number(org.id), name: org.name } as any);
    nav(`/${org.id}/dashboard`);
  };

  return (
    <div className="w-full min-w-0 flex flex-col h-full gap-3 p-4">
      {/* Filter chips */}
      <div className="flex-none flex max-w-full gap-2 flex-wrap">
        {states.map((state) => {
          const active = selectedState?.value === state.value;
          return (
            <button
              key={state.value}
              type="button"
              onClick={() => onStateChange(state)}
              className={cn(
                "inline-flex items-center h-6 px-2 rounded-full shrink-0 transition-colors",
                active ? "bg-brand-blue text-white" : "bg-brand-blue-light text-brand-blue",
              )}
            >
              <span className="text-xs font-medium whitespace-nowrap">{state.key}</span>
            </button>
          );
        })}
      </div>

      {/* Search + create */}
      <div className="flex-none flex items-center gap-3">
        <div className="relative shrink-0" style={{ width: 500 }}>
          <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            size="sm"
            placeholder={t(Namespace.Organizations, "searchPlaceholder")}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-9 !rounded-lg w-full"
          />
        </div>
        <button
          type="button"
          className="ml-auto inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-brand-blue-light text-brand-blue text-sm font-medium whitespace-nowrap hover:bg-[#c0d8ff] transition-colors"
          onClick={() => setAddOpen(true)}
        >
          <BrandIcPlus className="w-4 h-4 shrink-0" />
          {t(Namespace.Organizations, "createNew")}
        </button>
      </div>

      <div className="flex-none border-t border-gray-200 -mx-4" />

      {/* Cards */}
      <div ref={cardContainerRef} className="flex-1 min-h-0 overflow-hidden pt-1">
        {pageSize === 0 || isLoading || isFetching ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {Array.from({ length: pageSize || 8 }).map((_, i) => (
              <div key={i} className="bg-white rounded-lg border px-4 py-3 h-[90px] animate-pulse flex flex-col justify-between">
                <div className="h-4 w-2/3 bg-slate-200 rounded" />
                <div className="flex items-center gap-3">
                  <div className="h-4 w-16 bg-slate-200 rounded-full" />
                  <div className="h-3.5 w-24 bg-slate-200 rounded" />
                  <div className="h-3.5 w-12 bg-slate-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {organizations.map((org) => (
              <div
                key={String(org.id)}
                data-testid="organizations-card"
                onClick={() => onCardClick(org)}
                className="group bg-white rounded-lg border px-4 py-3 h-[90px] flex items-center gap-2 transition-shadow cursor-pointer hover:shadow-md hover:border-blue-300"
              >
                <div className="flex-1 min-w-0 flex flex-col gap-2">
                  <h3 className="font-semibold text-base leading-4 line-clamp-1 text-[#1d1f23]">
                    {org.name}
                  </h3>
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={cn(
                        "inline-flex items-center h-4 px-2 rounded-full text-[10px] font-semibold text-white whitespace-nowrap shrink-0",
                        getBadgeColor(org.type),
                      )}
                    >
                      {getTypeLabel(org.type, t)}
                    </span>
                    <p className="text-sm flex items-center min-w-0 shrink">
                      <span className="font-medium text-[#1d1f23] shrink-0">{t(Namespace.Organizations, "bin")} </span>
                      <span className="text-[#727986] truncate">{org.bin || "—"}</span>
                    </p>
                    <p className="text-sm flex items-center min-w-0 shrink">
                      <span className="font-medium text-[#1d1f23] shrink-0">{t(Namespace.Common, "id")}: </span>
                      <span className="text-[#727986] truncate">{String(org.id)}</span>
                    </p>
                  </div>
                </div>

                {/* Hover actions */}
                <div className="flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setEditOrg(org); }}
                    className="w-6 h-6 rounded-lg bg-[#d6e6ff] flex items-center justify-center"
                  >
                    <Pencil className="w-3 h-3 text-[#007aff]" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onCardClick(org); }}
                    className="w-6 h-6 rounded-lg bg-[#007aff] flex items-center justify-center"
                  >
                    <ArrowRight className="w-3 h-3 text-white" />
                  </button>
                </div>
              </div>
            ))}

            {organizations.length === 0 && (
              <div className="col-span-full text-center py-10 text-gray-500">
                {t(Namespace.Organizations, "notFound")}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex-none pt-2">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange}/>
        </div>
      )}

      <OrganizationDialog mode="create" open={addOpen} onOpenChange={setAddOpen} />
      <OrganizationDialog
        mode="edit"
        open={!!editOrg}
        onOpenChange={(v) => { if (!v) setEditOrg(null); }}
        org={editOrg}
      />
    </div>
  );
}
